import { Box, Flex, LinkBox, Tag, Text } from '@chakra-ui/react';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { useMemo } from 'react';

import { getReadingTime } from '#features/blog/utils/getReadingTime';
import { useFormatDistanceToNow } from '#features/date';
import { Image, Link } from '#features/ui';

type BlogPostPreviewProps = {
  slug: string;
  previewImage: string;
  title: string;
  tags?: string;
  date?: string;
  ogDescription?: string;
  content?: string;
};

export const BlogPostPreview = ({
  title,
  previewImage,
  slug,
  tags,
  date,
  ogDescription,
  content,
}: BlogPostPreviewProps) => {
  const { _ } = useLingui();
  const formatDistanceToNow = useFormatDistanceToNow();
  const readingTime = useMemo(() => (content ? getReadingTime(content) : null), [content]);

  return (
    <LinkBox
      display="flex"
      w="100%"
      overflow="hidden"
      borderRadius="xl"
      flexDir="column"
      bg="brand.700"
      transition="all 200ms"
      _hover={{ transform: 'translateY(-2px)', bg: 'brand.600' }}
      css={{
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none',
          '&:hover': { transform: 'none' },
        },
      }}
    >
      {/* Image */}
      <Box w="100%" aspectRatio="16 / 9" overflow="hidden">
        <Image
          src={previewImage}
          alt={title}
          style={{
            objectFit: 'cover',
            objectPosition: 'top center',
            width: '100%',
            height: '100%',
          }}
        />
      </Box>

      {/* Content */}
      <Flex direction="column" p={5} gap={3} flex={1}>
        {/* Tags + reading time */}
        <Flex gap={2} alignItems="center" flexWrap="wrap">
          {tags && (
            <>
              {tags
                .split(',')
                .slice(0, 2)
                .map((tag) => (
                  <Tag.Root key={tag} size="sm">
                    <Tag.Label textStyle="label">{tag}</Tag.Label>
                  </Tag.Root>
                ))}
            </>
          )}
          {readingTime && (
            <Text textStyle="xs" color="whiteAlpha.500">
              {readingTime} {_(msg({ id: 'blog.readingTime', message: 'min read' }))}
            </Text>
          )}
        </Flex>

        {/* Title */}
        <Text fontWeight="700" textStyle="lg" lineHeight="1.3" lineClamp={2}>
          {title}
        </Text>

        {/* Description */}
        {ogDescription && (
          <Text textStyle="sm" lineHeight="1.5" color="whiteAlpha.600" lineClamp={2}>
            {ogDescription}
          </Text>
        )}

        {/* Date */}
        <Flex mt="auto" pt={1}>
          {date && (
            <Text textStyle="xs" color="whiteAlpha.400">
              {formatDistanceToNow(date)}
            </Text>
          )}
        </Flex>
      </Flex>

      <Link to="/{-$locale}/blog/$slug" params={{ slug }} overlay />
    </LinkBox>
  );
};
